import React from 'react';
import { default as verizonAssets } from './SHS-WEB';
import { DynamicAssets, DynamicAssetsKeys, NavItem, ValuePropositionContent } from './models';
import { getPath } from '../pages/auth/utils';
import { AuthPath } from '../pages/auth/models';
import { openChat } from '../utils';
import { getPartnerResource } from '.';
import MoreInformationLink from './components/ValuePropositionLink';
import { LinkId } from '../constants';
import DynamicCallToAction from '../components/DynamicCallToAction';
import styled from 'styled-components';
import { CallToAction } from '../styling/styles';
import BREAK_POINTS from '../styling/media-queries';
import mixpanelClient from '../api/mixpanelClient';

const EXPERT_SESSIONS_PATH = '/expert-sessions';

const navLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuChat,
    label: 'Chat with an expert',
    onClick: () => openChat(),
  },
  {
    id: LinkId.HeaderMenuClaims,
    label: 'File a claim',
    to: '/dashboard',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'Changes coming in July',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuLogin,
    label: 'Log in',
    to: getPath(AuthPath.Login),
  },
];

const loggedInFooterLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuChat,
    label: 'Chat with an expert',
    onClick: () => openChat(),
  },
  {
    id: LinkId.HeaderMenuClaims,
    label: 'File a claim',
    to: '{{ claimUrl }}',
    target: '_blank',
  },
];

const loggedInHeaderLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuExpertSessions,
    label: 'Expert sessions',
    to: EXPERT_SESSIONS_PATH,
  },
  {
    id: LinkId.HeaderMenuDashboard,
    label: 'Dashboard',
    to: '/dashboard',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'Changes coming in July',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuLogout,
    label: 'Log out',
    to: getPath(AuthPath.Logout),
  },
];

export const valuePropositionContent: ValuePropositionContent[] = [
  {
    icon: getPartnerResource('get-refund-icon.svg'),
    caption: 'Get money back',
    title: (
      <>
        See if you can get some <br /> money back from old <br /> tech warranties
      </>
    ),
    subtitle: (
      <>
        With Verizon Protect Home, you may
        <br /> not need your old tech warranties
        <br /> anymore.
      </>
    ),
    points: [],
    image: getPartnerResource('get-refund-image.jpg'),
    imageMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={true} text="Show me how" to="/dashboard/warranty-selection" />,
    shouldShowBanner: false,
    label: 'refund',
  },
  {
    icon: getPartnerResource('value-prop-icon-1.svg'),
    caption: 'Protection & repairs',
    title: <>Worry-free protection</>,
    subtitle: <>Because accidents can happen to anyone.</>,
    points: [
      'Coverage regardless of age',
      <>Can&rsquo;t repair? We&rsquo;ll try to replace</>,
      <>Can&rsquo;t replace? We&rsquo;ll send a check for market value</>,
    ],
    image: getPartnerResource('value_prop_img_1.jpg'),
    imageMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="See everything we cover" to="/whats-covered" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-2.svg'),
    caption: 'Unlimited expert support',
    title: <>Experts you can talk to and understand</>,
    subtitle: <>No robots. No run-around.</>,
    points: [
      'U.S. based support',
      'Available 24/7, 365 days a year',
      'Millions of happy callers every month',
      'Our average rating is 4.8 out of 5 stars',
    ],
    image: getPartnerResource('value_prop_img_2.jpg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    // link: <MoreInformationLink text="See how support works" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-3.svg'),
    caption: 'No paperwork or receipts',
    title: <>All of the coverage</>,
    subtitle: <>None of the hassle.</>,
    points: [
      <>It doesn&rsquo;t matter where or when you bought your device</>,
      'No expiration dates to keep track of',
      'No receipts to hunt down',
    ],
    image: getPartnerResource('value_prop_img_3.jpg'),
    imageMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="See everything we cover" to="/whats-covered" />,
  },
];

const ProtectItCta = styled(DynamicCallToAction)`
  ${CallToAction}
  font-weight: 600;
  font-size: 16px;
  margin-top: 22px;
  @media ${BREAK_POINTS.mobileBig} {
    font-size: 20px;
    padding-left: 50px;
    padding-right: 50px;
  }
`;

const ProtectItCtaOnClick = () => {
  mixpanelClient.track('TCH_HOME_ENROLL_NOW_CLICK');
  document.location.href = 'https://www.verizon.com/solutions-and-services/verizon-protect/home/';
};

const assets: DynamicAssets = {
  ...verizonAssets,
  [DynamicAssetsKeys.expertChatAppKey]: 'e4496328-f144-4a74-a6f8-e73c5f614989',
  [DynamicAssetsKeys.loggedInFooterNavLinks]: loggedInFooterLinks,
  [DynamicAssetsKeys.footerNavLinks]: navLinks,
  [DynamicAssetsKeys.homeValuePropContent]: valuePropositionContent,
  [DynamicAssetsKeys.homeProtectItSubtitle2]: 'For just $25/month',
  [DynamicAssetsKeys.homeProtectItCtaComponent]: (props: any) => (
    <ProtectItCta {...props} onClick={ProtectItCtaOnClick} to="">
      Enroll now
    </ProtectItCta>
  ),
  [DynamicAssetsKeys.headerMobileCtaComponent]: () => <></>,
  [DynamicAssetsKeys.headerNavLinks]: navLinks,
  [DynamicAssetsKeys.loggedInHeaderNavLinks]: loggedInHeaderLinks,
};

export default assets;



// WEBPACK FOOTER //
// ./packages/smart-home-support/assets/TCH-WEB.tsx